import AIChatWidget from '@/components/aichat/AiChatWidget';
import GovernanceCard from '@/components/landing/governance-card';
import PageHero from '@/components/landing/page-hero';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Scale, Bot, Download, HandCoins, Landmark, ShieldCheck, ShieldEllipsis, UserRoundCheck } from 'lucide-react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

const governanceCards = [
    {
        icon: Scale,
        title: 'Code of Ethics',
        description: 'Review the institutional standards that guide professional conduct, accountability, and responsible decision-making.',
        href: '/downloads?category=Policies&query=Code%20of%20Ethics',
    },
    {
        icon: HandCoins,
        title: 'Anti-Corruption Policy',
        description: 'Learn how Northeastern College promotes integrity, transparency, and safeguards against corruption risks.',
        href: '/downloads?category=Policies&query=Anti-Corruption',
    },
    {
        icon: UserRoundCheck,
        title: 'Conflict of Interest Policy',
        description: 'Understand expectations for disclosure, impartiality, and ethical conduct in institutional relationships.',
        href: '/downloads?category=Policies&query=Conflict%20of%20Interest',
    },
    {
        icon: Bot,
        title: 'AI Policy',
        description: 'See how the college is preparing guidance for safe, responsible, and mission-aligned use of AI technologies.',
        href: '/downloads?category=Policies&query=AI%20Policy',
    },
    {
        icon: ShieldCheck,
        title: 'Data Privacy Policy',
        description: 'Explore the principles and practices that protect personal data and privacy across the college community.',
        href: '/downloads?category=Policies&query=Data%20Privacy',
    },
    {
        icon: ShieldEllipsis,
        title: 'Integrity & Whistleblowing System',
        description: 'Access the public reporting channel for suspected misconduct, unethical behavior, and institutional concerns.',
        href: '/governance/integrity',
    },
    {
        icon: Download,
        title: 'Download Center',
        description: 'Find policies, forms, handbooks, reports, and other official files in one searchable public repository.',
        href: '/downloads',
    },
];

export default function Governance() {
    return (
        <LandingLayout>
            <SeoHead
                title="Governance"
                description="Explore Northeastern College governance resources, policies, accountability commitments, and public integrity channels."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section className="bg-muted pb-16">
                <PageHero
                    title="Governance"
                    description="Northeastern College is committed to ethical leadership, accountability, transparency, institutional integrity, and responsible governance."
                    breadcrumbs={[
                        { label: 'Home', href: '/' },
                        { label: 'About' },
                        { label: 'Governance' },
                    ]}
                    image="/images/hero/about.png"
                    imageAlt="Northeastern College governance"
                />

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    <div className="bg-card border-border mb-8 rounded-2xl border p-6 shadow-sm md:p-8">
                        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                            <div className="max-w-3xl">
                                <div className="mb-3 inline-flex rounded-full bg-[#0A7A35]/10 px-3 py-1 text-xs font-bold tracking-wider text-[#0A7A35] uppercase">
                                    Governance Hub
                                </div>
                                <h2 className="text-foreground text-2xl font-bold md:text-3xl">Policies, ethics, accountability, and public access in one place</h2>
                                <p className="text-muted-foreground mt-3 text-sm leading-relaxed md:text-base">
                                    This section is designed as a central hub for governance-related policies and integrity resources, with room for
                                    future Laravel-backed document management and reporting workflows.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                        {governanceCards.map((card) => (
                            <GovernanceCard key={card.title} icon={card.icon} title={card.title} description={card.description} href={card.href} />
                        ))}
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
